import { useEffect, useState } from "react";
import { Link, useLocation } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { Flame, AlarmClock, X, ArrowRight } from "lucide-react";
import api from "@/lib/api";
import { useI18n } from "@/context/I18nContext";

const todayKey = () => `fluent_nudge_${new Date().toISOString().slice(0, 10)}`;

export default function NudgeBanner() {
  const { t } = useI18n();
  const [nudge, setNudge] = useState(null);
  const [dismissed, setDismissed] = useState(false);
  const onReview = useLocation().pathname === "/review";

  useEffect(() => {
    if (localStorage.getItem(todayKey())) { setDismissed(true); return; }
    api.get("/nudges").then((r) => setNudge(r.data)).catch((e) => console.warn("nudges failed", e));
  }, []);

  if (dismissed || !nudge || onReview) return null;
  const { words_due, streak_at_risk, streak_count } = nudge;
  if (!words_due && !streak_at_risk) return null;

  const streakMode = streak_at_risk;
  const close = () => { localStorage.setItem(todayKey(), "1"); setDismissed(true); };

  const cfg = streakMode
    ? { bg: "bg-butter", icon: Flame, to: "/today",
        title: t("nudge.streakTitle", { count: streak_count }),
        sub: t("nudge.streakSub"), cta: t("nudge.startLoop") }
    : { bg: "bg-coral text-white", icon: AlarmClock, to: "/today",
        title: t("nudge.dueTitle", { count: words_due }),
        sub: t("nudge.dueSub"), cta: t("nudge.reviewNow") };

  const Icon = cfg.icon;
  return (
    <AnimatePresence>
      <motion.div initial={{ height: 0, opacity: 0 }} animate={{ height: "auto", opacity: 1 }} exit={{ height: 0, opacity: 0 }}
        className="border-b-2 border-ink overflow-hidden" data-testid="nudge-banner">
        <div className={`${cfg.bg}`}>
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-3 flex items-center gap-3">
            <div className="w-9 h-9 rounded-lg border-2 border-ink bg-white/90 flex items-center justify-center shrink-0">
              <Icon className="w-5 h-5 stroke-[2.5] text-ink" />
            </div>
            <div className="flex-1 min-w-0">
              <div className="font-cabinet font-bold leading-tight truncate">{cfg.title}</div>
              <div className="text-xs opacity-80 truncate">{cfg.sub}</div>
            </div>
            <Link to={cfg.to} onClick={close} data-testid="nudge-cta"
              className="nb-btn-outline text-ink px-4 py-2 text-sm shrink-0 hidden sm:inline-flex">
              {cfg.cta} <ArrowRight className="w-4 h-4 rtl:rotate-180" />
            </Link>
            <button onClick={close} data-testid="nudge-dismiss" className="w-8 h-8 rounded-lg border-2 border-ink bg-white/90 flex items-center justify-center shrink-0">
              <X className="w-4 h-4 text-ink" />
            </button>
          </div>
        </div>
      </motion.div>
    </AnimatePresence>
  );
}
